import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Maps Tripetto nodes and slots to their converted Formly fields
 * Used in two-pass conversion to reference actual Formly field data
 */
export class NodeToFieldMap {
	// Map of node ID to Formly field
	private nodeToField = new Map<string, FormlyFieldConfig>();

	// Map of slot ID to Formly field key
	private slotToFieldKey = new Map<string, string>();

	// Map of slot ID to field metadata
	private slotMetadata = new Map<
		string,
		{
			alias?: string;
			slotId: string;
			nodeName?: string;
			nodeId: string;
		}
	>();

	/**
	 * Register a Formly field for a Tripetto node
	 */
	registerField(nodeId: string, field: FormlyFieldConfig): void {
		this.nodeToField.set(nodeId, field);
	}

	/**
	 * Register a slot mapping to a field key
	 */
	registerSlot(
		slotId: string,
		fieldKey: string,
		metadata: { alias?: string; nodeName?: string; nodeId: string }
	): void {
		this.slotToFieldKey.set(slotId, fieldKey);
		this.slotMetadata.set(slotId, {
			...metadata,
			slotId
		});
	}

	/**
	 * Get the Formly field for a node ID
	 */
	getFieldByNodeId(nodeId: string): FormlyFieldConfig | undefined {
		return this.nodeToField.get(nodeId);
	}

	/**
	 * Get the field key for a slot ID
	 */
	getFieldKeyBySlotId(slotId: string): string | undefined {
		return this.slotToFieldKey.get(slotId);
	}

	/**
	 * Get the Formly field for a slot ID
	 */
	getFieldBySlotId(slotId: string): FormlyFieldConfig | undefined {
		const metadata = this.slotMetadata.get(slotId);
		if (!metadata) return undefined;
		return this.nodeToField.get(metadata.nodeId);
	}

	/**
	 * Get slot metadata
	 */
	getSlotMetadata(slotId: string): { alias?: string; slotId: string; nodeName?: string; nodeId: string } | undefined {
		return this.slotMetadata.get(slotId);
	}

	/**
	 * Get all registered fields
	 */
	getAllFields(): FormlyFieldConfig[] {
		return Array.from(this.nodeToField.values());
	}
}
